import DocumentReference from "@/models/conversation/document";

const REFERENCE_REGEX = /(\[[^\]]+\]\((\d+)\))/g;
const SIMPLE_REFERENCE_REGEX = /\[[^\]]+\]\((\d+)\)/g;

export const dedupeReferencesByChunkId = (
  references: DocumentReference[]
): DocumentReference[] => {
  const seen = new Set<string>();
  return references.filter((reference) => {
    if (seen.has(reference.chunkId)) {
      return false;
    }

    seen.add(reference.chunkId);
    return true;
  });
};

export const enrichReferencesWithWorkspaceIds = (
  references: DocumentReference[],
  documentWorkspaceMap: Record<string, string>
): DocumentReference[] =>
  references.map((reference) => ({
    ...reference,
    workspaceId: reference.documentId
      ? documentWorkspaceMap[reference.documentId] ?? ""
      : "",
  }));

export const addReferenceNumber = (
  input: string,
  replacements: Record<string, string>,
  references: DocumentReference[]
): Record<string, string> => {
  const nextReplacements = { ...replacements };
  const matches = input.matchAll(SIMPLE_REFERENCE_REGEX);

  for (const match of matches) {
    const fullMatch = match[0];
    const indexMatch = match[1];
    if (!fullMatch || !indexMatch) {
      continue;
    }

    const reference = references.find(
      (item) => item.referenceId === Number(indexMatch)
    );
    if (!reference) {
      // chat-api dropped this citation (e.g. the chunk had no usable documentId).
      // Leave it unnumbered so the text does not show a [n] with no sidebar entry.
      continue;
    }

    const indexReplace = `](${indexMatch})`;
    const displayIndex = references.indexOf(reference) + 1;

    nextReplacements[fullMatch] = fullMatch.replace(
      indexReplace,
      `(${displayIndex})${indexReplace}`
    );
  }

  return nextReplacements;
};

export const replaceForDict = (
  replacements: Record<string, string>,
  input: string
): string => {
  if (Object.keys(replacements).length === 0) {
    return input;
  }

  return input.replace(REFERENCE_REGEX, (match) => replacements[match] ?? match);
};

export type ReferenceProcessingResult = {
  references: DocumentReference[];
  replacements: Record<string, string>;
  text: string;
};

export const processReferences = ({
  text,
  references,
  replacements,
  documentWorkspaceMap,
}: {
  text: string;
  references: DocumentReference[];
  replacements: Record<string, string>;
  documentWorkspaceMap: Record<string, string>;
}): ReferenceProcessingResult => {
  const dedupedReferences = dedupeReferencesByChunkId(references);
  const enrichedReferences = enrichReferencesWithWorkspaceIds(
    dedupedReferences,
    documentWorkspaceMap
  );
  const nextReplacements = addReferenceNumber(
    text,
    replacements,
    enrichedReferences
  );

  return {
    references: enrichedReferences,
    replacements: nextReplacements,
    text: replaceForDict(nextReplacements, text),
  };
};
